
import React from 'react';
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { Application } from '@/types/database.types';

interface MedicalQuestionnaireProps {
  register: UseFormRegister<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
}

interface MedicalConditionProps {
  id: keyof Application;
  label: string;
  register: UseFormRegister<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  detailsField: string;
  placeholder?: string;
}

const MedicalCondition = ({ id, label, register, setValue, watch, detailsField, placeholder = "Please provide details..." }: MedicalConditionProps) => {
  const value = watch(id) as boolean | undefined;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={id as string} className="font-medium text-foreground">
          {label}
        </Label>
        <Switch
          id={id as string}
          checked={value === true}
          onCheckedChange={(checked) => setValue(id, checked)}
        />
      </div>
      
      {value && (
        <div className="pl-5 border-l-2 border-primary/20 mt-2">
          <Textarea
            id={detailsField}
            {...register(detailsField as any)}
            placeholder={placeholder}
            className="mt-1"
          />
        </div>
      )}
    </div>
  );
};

export const MedicalQuestionnaire = ({ register, setValue, watch }: MedicalQuestionnaireProps) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Medical History Questionnaire</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Please indicate if you are personally aware via direct and voluntary communication (without assuming) of any employees or dependents currently experiencing any of the following medical conditions:
        </p>
      </div>

      <div className="space-y-4">
        <MedicalCondition
          id="has_kidney_disease"
          label="Kidney Disease"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="kidney_disease_details"
          placeholder="Please describe the kidney condition, treatment status, and any ongoing care..."
        />

        <MedicalCondition
          id="has_diabetes"
          label="Diabetes"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="diabetes_details"
          placeholder="Please specify the type of diabetes, management approach, and current status..."
        />

        <MedicalCondition
          id="has_liver_disease"
          label="Liver Disease"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="liver_disease_details"
          placeholder="Please describe the liver condition, severity, and treatment plan..."
        />

        <MedicalCondition
          id="has_neural_disorder"
          label="Neural Disorders"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="neural_disorder_details"
          placeholder="Please specify the neural disorder type, symptoms, and management approach..."
        />

        <MedicalCondition
          id="has_cardiovascular"
          label="Cardiovascular Disease"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="cardiovascular_details"
          placeholder="Please describe the cardiovascular condition, treatment, and current status..."
        />

        <MedicalCondition
          id="has_respiratory_disease"
          label="Respiratory Disease"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="respiratory_disease_details"
          placeholder="Please specify the respiratory condition, severity, and treatment approach..."
        />

        <MedicalCondition
          id="has_musculoskeletal"
          label="Musculoskeletal Disorders"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="musculoskeletal_details"
          placeholder="Please describe the musculoskeletal disorder, affected areas, and treatment..."
        />

        <MedicalCondition
          id="has_transplants"
          label="Organ Transplants"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="transplants_details"
          placeholder="Please specify the organ(s) transplanted, date, and current status..."
        />

        <MedicalCondition
          id="has_cancer"
          label="Cancer"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="cancer_details"
          placeholder="Please describe the cancer type, stage, treatment status, and prognosis..."
        />

        <MedicalCondition
          id="has_hiv_aids"
          label="HIV/AIDS"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="hiv_aids_details"
          placeholder="Please provide information about current treatment and health status..."
        />

        <MedicalCondition
          id="has_mental_health"
          label="Mental Health Conditions"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="mental_health_details"
          placeholder="Please describe the mental health condition, treatment approach, and current status..."
        />

        <MedicalCondition
          id="has_nervous_system"
          label="Nervous System Disorders"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="nervous_system_details"
          placeholder="Please specify the nervous system disorder, symptoms, and management plan..."
        />

        <MedicalCondition
          id="has_back_spinal_disorder"
          label="Back/Spinal Disorder"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="back_spinal_disorder_details"
          placeholder="Please describe the back or spinal condition, treatment, and impact on daily activities..."
        />

        <MedicalCondition
          id="has_intestinal_disorder"
          label="Intestinal Disorder"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="intestinal_disorder_details"
          placeholder="Please specify the intestinal disorder, symptoms, and treatment approach..."
        />

        <MedicalCondition
          id="has_heart_disease"
          label="Heart Disease"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="heart_disease_details"
          placeholder="Please describe the heart condition, treatment, and current cardiac status..."
        />

        <MedicalCondition
          id="has_diabetes_type"
          label="Diabetes (Type I or II)"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="diabetes_type_details"
          placeholder="Please specify Type I or II, management method, and blood sugar control status..."
        />

        <MedicalCondition
          id="has_stroke"
          label="Stroke"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="stroke_details"
          placeholder="Please describe the stroke type, date, recovery status, and ongoing effects..."
        />

        <MedicalCondition
          id="has_tumor"
          label="Tumor"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="tumor_details"
          placeholder="Please specify the tumor type, location, treatment status, and prognosis..."
        />

        <MedicalCondition
          id="has_active_pregnancy"
          label="Active Pregnancy"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="active_pregnancy_details"
          placeholder="Please provide pregnancy details including due date and any complications..."
        />

        <MedicalCondition
          id="has_premature_birth"
          label="Premature Birth Complications"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="premature_birth_details"
          placeholder="Please describe the premature birth complications and ongoing care needs..."
        />

        <MedicalCondition
          id="has_other_conditions"
          label="Other Medical Conditions"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="other_conditions_details"
          placeholder="Please describe any other medical conditions not listed above..."
        />
      </div>
    </div>
  );
};
